import { Form, TextInput, SubmitButton } from '@components/shared/forms';
import { CustomIcon, Button, Notification } from '@components/shared';
import routes from '@root/routes';
import * as Yup from 'yup';
import {
  ErrorResponse,
  TourbookSummary,
  SearchListing,
  Listing,
  BuildingListing,
} from '@root/types';
import { useTranslation, Trans } from 'react-i18next';
import s from './TourbookOverlay.module.less';

type Props = {
  close: () => void;
  createOnly: boolean;
  createTourbook: (
    args: {
      name: string;
      listingId?: string;
      listing: SearchListing | Listing | BuildingListing | undefined;
    },
    opts: {
      onSuccess: (tourbook: TourbookSummary) => void;
      onError: (errorResponse: ErrorResponse) => void;
    },
  ) => void;
  goToInfoCard: () => void;
  goToToggleCard: () => void;
  listing?: SearchListing | Listing | BuildingListing;
};

export default function CreateTourbookCard({
  close,
  createTourbook,
  createOnly,
  goToInfoCard,
  goToToggleCard,
  listing,
}: Props) {
  const { t } = useTranslation('tourbook');

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .max(90, t('manageDialogs.nameMaxMessage'))
      .required(t('manageDialogs.requiredNameMessage')),
  });
  type FormValues = Yup.InferType<typeof validationSchema>;
  const initialValues = { name: '' } as FormValues;

  return (
    <div data-testid="addTourbookCard" className={s.addCard}>
      <div className={s.navButtons}>
        <button
          data-testid="goToInfoButton"
          type="button"
          onClick={goToInfoCard}
          className={s.headerButton}
        >
          <CustomIcon className={s.infoButton} type="info" />
        </button>
        <button onClick={close} type="button">
          <CustomIcon type="close" />
        </button>
      </div>
      <h3>{t('manageDialogs.createATourbook')}</h3>
      <CustomIcon className={s.tourbookIcon} type="tourbook" />
      <Form<FormValues>
        id="createTourbook"
        className={s.createForm}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values, helpers) => {
          await createTourbook(
            { name: values.name!, listingId: listing?.id, listing },
            {
              onSuccess: tourbook => {
                helpers.setFieldValue('name', '', false);
                if (!createOnly) {
                  goToToggleCard();
                  /* eslint-disable react/jsx-no-literals */
                  Notification.info({
                    placement: 'bottomLeft',
                    duration: 3.5,
                    title: t('toasts.tourbookCreatedTitle'),
                    text: (
                      <Trans i18nKey="toasts.tourbookCreatedText" ns="tourbook">
                        View your new <a href={routes.tourbook(tourbook.id)}>Tourbook</a>
                      </Trans>
                    ),
                  });
                  /* eslint-enable react/jsx-no-literals */
                }
              },
              onError: ({ errors }: ErrorResponse) => {
                errors.forEach(error => {
                  helpers.setFieldError(error.field!, error.message);
                });
              },
            },
          );
        }}
      >
        <TextInput
          name="name"
          labelText={t('manageDialogs.name')}
          containerClass={s.nameFieldContainer}
          autoComplete={false}
          showCharacterCount
          maxLength={90}
        />
        <div className={s.ctaButtons}>
          {!createOnly ? (
            <Button size="large" className={s.backButton} type="secondary" onClick={goToToggleCard}>
              {t('manageDialogs.back')}
            </Button>
          ) : (
            <Button size="large" className={s.backButton} type="secondary" onClick={close}>
              {t('listingCard.listingDescription.cancel')}
            </Button>
          )}
          <SubmitButton
            className={s.createTourbookButton}
            size="large"
            data-testid="createTourbookButton"
          >
            {t('manageDialogs.createButtonText')}
          </SubmitButton>
        </div>
      </Form>
    </div>
  );
}
